import 'typescript-array-extensions';
import './../../extensions/number';
import './../../extensions/string';
import { Filter } from './filter';
import { Search } from './search';
const bedsJSON = require('./beds.json');

export namespace Hotel {
  export interface SearchProps {
    minPrice: string;
    searchFinish: string;
  }

  export interface HotelResponse {
    result: HotelResultV2;
    currency?: string;
    locale?: string;
  }

  export interface HotelResultV2 {
    groups: Group[];
    filters: FilterSection[];
    request_id: string;
    hotel: {
      currency?: string;
      reviews: Review[];
    };
  }

  export interface FilterSection {
    header: string;
    options: OfferFilter[];
  }

  export class FilterSectionModel {
    header: string;
    options: Filter.Option[];

    constructor(header: string, options: Filter.Option[]) {
      this.header = header;
      this.options = options;
    }

    static fromFilterSection(section: FilterSection): FilterSectionModel {
      return new FilterSectionModel(
        section.header,
        section.options.map(
          it =>
            new Filter.Option({
              id: it.id,
              name: it.value_display,
            }),
        ),
      );
    }

    clear() {
      this.options.forEach(it => (it.selected = false));
    }
  }

  export class OfferFilter {
    id?: number | string;
    value_display: string;
  }

  export enum OPTION_STATIC_ID {
    FREE_BREAKFAST = 1,
    FREE_CANCELATION = 2,
    PAY_ONLINE_NOW = 3,
    PAY_LATER_AT_HOTEL = 4,
    ELIGIBLE_FOR_POINTS = 5,
    PARTIALLY_REFUNDABLE = 10,
    NON_REFUNDABLE = 11,
  }

  export interface Amenities {
    list: Amenitiy[];
    featured: Amenitiy[];
  }

  export interface Amenitiy {
    id: string;
    title: string;
  }

  export interface HotelInfoResponse {
    status: string;
    hotels: HotelDetailsV2[];
    amenities: Amenitiy[];
    featured: Amenitiy[];
  }

  export interface HotelDetailsV2 {
    id: string | number;
    author?: string;
    bg_color?: string;
    stars: number;
    title: string;
    location: Location;
    images: Image[];
    reviews_count: number;
    checkin_time: string;
    checkin_to: string;
    checkout_from: string;
    checkout_time: string;
    amenities: string[];
    featured: string[];
    rating: RatingV2;
    description: string;
    hotel_info: HotelInfo[];
    fp_hotel_score: number;
    all_reviews: TextReview[];
    currency?: string;
    media_source?: string;
    thumb?: string;
    thumb_preview?: string;
    website?: string;
    city_id?: string;
    hotel_chain?: any;
    hotel_id?: string;
    hotel_type?: string;
    hotel_url?: string;
    nps?: number;
    phone?: string;
    policies: Policies;
    publication_score?: number;
    publications?: any[];
    publications_count?: number;
    reward_program?: string;
    summary?: string;
    themes?: any[];
    is_genius?: BookingGenius;
  }

  export interface Policies {
    checkin: PolicyCheckin;
    fees: PolicyFees;
    know_before_you_go: string;
  }

  export interface PolicyCheckin {
    instructions: string;
    min_age: number;
    special_instructions: string;
  }

  export interface PolicyFees {
    mandatory?: string;
    optional?: string;
  }

  export interface Review {
    category_id: number;
    name: string;
    count: number;
    score: number;
    type: string;
    color: string;
    default_color: string;
    opacity: number;
  }

  export interface TextReview {
    rating_date: string;
    review: string;
  }

  export interface Location {
    lat: number;
    lon: number;
    district?: string;
    distance_from?: string;
    distance?: number;
    country: string;
    country_name?: string;
    state: string;
    city: string;
    city_id?: number;
    zip?: string;
    address?: string;
  }

  export interface Rating {
    count: number;
    max: number;
    title: string;
  }

  export interface RatingV2 {
    ratingCount: number;
    ratingMax: number;
    ratingText: string;
  }

  export interface HotelInfo {
    title: string;
    paragraphs: Paragraph[];
  }

  export interface Paragraph {
    title?: string;
    text?: string;
    list?: string[];
  }

  export class Group {
    name: string;
    size_ft: number;
    size: number;
    offers: OfferV2[];
    public_prices: PublicPrice[];
    images?: string[];

    constructor(group: Group) {
      this.name = group.name;
      this.size_ft = group.size_ft;
      this.size = group.size;
      this.offers = group.offers.map(offer => new OfferV2(offer));
      this.public_prices = group.public_prices;
      this.images = group.images;
    }
  }

  export interface PublicPrice {
    link: string;
    price: number;
    price_tag: PublicPriceTags;
    source: PublicPriceSource;
  }

  export enum PublicPriceTags {
    STAY = 'stay',
    SOLDOUT = 'soldout',
  }

  export enum PublicPriceSource {
    BOOKING = 'Booking',
    EXPEDIA = 'Expedia',
  }

  export enum BookingGenius {
    UNKNOWN = '-1',
    UNAVAILABLE = '0',
    AVAILABLE = '1',
  }

  export enum OrderFlow {
    Full = 'full',
    Redirect = 'redirect',
  }

  export class OfferV2 {
    name: string;
    meal_name: string;
    bed: string;
    bed_origin?: string;
    filter_ref: number[];
    refundable: boolean;
    partial_refundable?: boolean;
    options: OfferFilter[];
    room: string;
    group_name: string;
    payment_description: string;
    policies: HotelRoomPolicies;
    size_ft?: number;
    prices: Prices;
    prepay: boolean;
    free_breakfast: boolean;
    pay_type_text: string;
    supplier: string;
    supplier_meta_data?: any;
    view_full: string;
    room_format_desc?: any;
    preauthorize: boolean;
    images: string[];
    loading: boolean = false;
    soldout: boolean = false;
    muted: boolean = false;
    can_be_paid_by_credits: boolean = false;
    cancelledBy: string = '';
    order_flow: OrderFlow;

    constructor(offer: OfferV2) {
      this.name = offer.name;
      this.meal_name = offer.meal_name;
      this.bed = offer.bed;
      this.bed_origin = offer.bed_origin ?? offer.bed;
      this.filter_ref = offer.filter_ref;
      this.refundable = offer.refundable;
      this.partial_refundable = offer.partial_refundable;
      this.options = offer.options;
      this.room = offer.room;
      this.group_name = offer.group_name;
      this.payment_description = offer.payment_description;
      this.policies = offer.policies;
      this.size_ft = offer.size_ft;
      this.prices = offer.prices;
      this.prepay = offer.prepay;
      this.free_breakfast = offer.free_breakfast;
      this.pay_type_text = offer.pay_type_text;
      this.supplier = offer.supplier;
      this.supplier_meta_data = offer.supplier_meta_data;
      this.view_full = offer.view_full;
      this.room_format_desc = offer.room_format_desc;
      this.preauthorize = offer.preauthorize;
      this.images = offer.images;
      this.loading = false;
      this.soldout = false;
      this.muted = false;
      this.cancelledBy = '';
      this.order_flow = offer.order_flow ?? OrderFlow.Full;
      this.can_be_paid_by_credits = offer.prices.enable_credits !== undefined;
    }
  }

  export interface Prices {
    supplements: Supplement[];
    disable_credits: Credits;
    enable_credits?: Credits;
  }

  export interface Credits {
    billable_currency: string;
    billable_pay_at_hotel: number;
    billable_pay_now: number;
    billable_price: number;
    billable_supplements_sum: number;
    market_price: boolean;
    price_with_taxes_and_fee: number;
    price_with_extra_savings_with_taxes: number;
    price_with_extra_savings: number;
    price: number;
    supplements_and_taxes: number;
    supplements_sum: number;
    taxes: number;
    public_price: number;
    price_with_extra_savings_per_night: number;
    taxes_per_night: number;
    public_price_with_taxes: number;
    public_price_per_night: number;
    public_price_per_night_with_taxes: number;
    credits: number;
    discount: number;
    public: boolean;
    cashback: number;
    saving: number;
    trip_total: number;
    pay_now: number;
    pay_at_hotel: number;
    token: string;
  }

  export interface BookingPolicy {
    result: {
      refundable: boolean;
      deposit_required: boolean;
      deposit_info?: string;
      cancellation_info?: string;
      currency: string;
      deposit_policies?: HotelRoomPolicyFine[];
      cancellation_policies?: HotelRoomPolicyFine[];
      is_3ds: boolean;
      payment_schedule?: PaymentSchedule[];
      is_3ds_on_prepay: boolean;
      supplier_meta_data?: any;
    };
  }

  export enum PaymentScheduleTypes {
    PayNow = 'pay_now',
    PayAtProperty = 'pay_at_property',
  }

  export interface PaymentSchedule {
    billable_currency: string;
    billable_price: number;
    price: number;
    tag: PaymentScheduleTypes;
    from: string;
  }

  export interface HotelRoomPolicyFine {
    from: string;
    price: number;
    billable_price: number;
    billable_currency: string;
  }

  export interface HotelRoomPolicies {
    free?: string;
    fault?: string;
    fine: HotelRoomPolicyFine[];
  }

  export interface HotelRoomPolicyFine {
    currency: string;
    from: string;
    to: string;
    amount: number;
  }

  export class Offer {
    fee: number;
    discount: number;
    taxes: number;
    public_price_supplier: string;
    checkin: string;
    checkout: string;
    public_price_per_night: number;
    price: number;
    public_price_with_taxes: number;
    images: string[];
    trip_total: number;
    group_name: string;
    room_desc?: string;
    policies: HotelRoomPolicies;
    price_with_extra_savings_per_night: number;
    price_with_extra_savings_with_taxes: number;
    supplements: Supplement[];
    supplements_sum: number;
    saving: number;
    breakfast: boolean;
    meal_name: string;
    discount_total: number;
    bed_configurations: BedConfigurations[];
    size_ft: number;
    price_with_extra_savings: number;
    price_with_taxes_and_fee: number;
    credits: number;
    payment_description: string;
    price_per_night: number;
    pay_type: string;
    refundable: boolean;
    view_full?: string;
    public_price: number;
    room: string;
    token: string;
    cashback: number;
    cashback_total: number;
    sale: number;

    constructor(offer: Offer) {
      this.fee = offer.fee;
      this.discount = offer.discount;
      this.taxes = offer.taxes;
      this.public_price_supplier = offer.public_price_supplier;
      this.checkin = offer.checkin;
      this.checkout = offer.checkout;
      this.public_price_per_night = offer.public_price_per_night;
      this.price = offer.price;
      this.public_price_with_taxes = offer.public_price_with_taxes;
      this.images = offer.images;
      this.trip_total = offer.trip_total;
      this.group_name = offer.group_name;
      this.room_desc = offer.room_desc;
      this.price_with_extra_savings_per_night =
        offer.price_with_extra_savings_per_night;
      this.price_with_extra_savings_with_taxes =
        offer.price_with_extra_savings_with_taxes;
      this.supplements = offer.supplements;
      this.supplements_sum = offer.supplements_sum;
      this.saving = offer.saving;
      this.breakfast = offer.breakfast;
      this.meal_name = offer.meal_name;
      this.discount_total = offer.discount_total;
      this.bed_configurations = offer.bed_configurations;
      this.size_ft = offer.size_ft;
      this.price_with_extra_savings = offer.price_with_extra_savings;
      this.price_with_taxes_and_fee = offer.price_with_taxes_and_fee;
      this.credits = offer.credits;
      this.payment_description = offer.payment_description;
      this.price_per_night = offer.price_per_night;
      this.pay_type = offer.pay_type;
      this.refundable = offer.refundable;
      this.view_full = offer.view_full;
      this.public_price = offer.public_price;
      this.room = offer.room;
      this.token = offer.token;
      this.cashback = offer.cashback;
      this.cashback_total = offer.cashback_total;
      this.sale = offer.sale;
      this.policies = offer.policies;
    }

    // paymentDescription(): string {
    //   return this.payment_description.replaceAll('\\*{2}(.+?)\\*{2}', '<b>\$1</b>');
    // }

    getFacilities(): string[] {
      const facilities = [];

      facilities.push(this.bed_configurations[0].description);
      facilities.push(this.pay_type === 'postpay' ? 'Pay at Hotel' : 'Pay Now');
      facilities.push(this.refundable ? 'Free Cancellation' : 'Non-Refundable');

      if (this.breakfast) facilities.push(this.meal_name);
      if (this.view_full) facilities.push(this.view_full);

      return facilities;
    }

    getSaving(): string {
      const saving = this.saving.formatCurrency({
        round: true,
      });

      return this.discount > 0
        ? `Including ${saving} off/stay`
        : `${saving} cash back/stay`;
    }
  }

  export interface Facility {
    category_name: string;
    id: number;
    names?: string[];
  }

  export interface Address {
    zip: string;
    country: string;
    address: string;
    city: string;
    district?: string;
    state: string;
    cityId: number;
  }

  export interface Supplement {
    name: string;
    price: number;
  }

  export interface Geo {
    lat: number;
    lng: number;
  }

  export interface Image {
    url: string;
  }

  export interface BedConfigurations {
    id: number;
    description: string;
  }

  export class Saving {
    enable: boolean = true;
    saving: number = 0;
    plan: string = '';

    constructor() {}
  }

  export class ConfirmLabel {
    isDivider: boolean;
    left: Label;
    right: Label;
    ref: string;

    constructor(
      left?: Label,
      right?: Label,
      isDivider?: boolean,
      ref?: string,
    ) {
      this.isDivider = isDivider || false;
      this.left = left || new Label('');
      this.right = right || new Label('');
      this.ref = ref || null;
    }
  }

  export class Label {
    value: string;
    isBold: boolean = false;
    color: string;

    constructor(value: string, isBold?: boolean, color?: string) {
      this.value = value;
      this.isBold = isBold || false;
      this.color = color;
    }
  }

  export interface Media {
    bgcolor?: string;
    thumbnail?: string;
    images: string[];
  }

  export interface SupplierLinkModel {
    link: string;
    showWindow: boolean;
    supplier: PublicPriceSource;
    isGenius: boolean;
    title: string;
    description: string;
    btn: string;
  }

  export interface BedPriority {
    name: string;
    priority: number[];
  }

  export const bedPriorityByGuest = (
    guest: number,
    bedName: string,
  ): number => {
    const bedsList = bedsJSON as BedPriority[];
    const bed = bedsList.find(
      it => it.name.toLowerCase() === bedName.toLowerCase(),
    );

    if (!bed || guest < 1 || guest > 4) {
      return 999;
    }

    return bed.priority[guest - 1];
  };

  export const bedSortingFunc = (
    a: string,
    b: string,
    guest: number,
  ): number => {
    return bedPriorityByGuest(guest, a) - bedPriorityByGuest(guest, b);
  };

  export const cancellationPriority = (offer: OfferV2): number => {
    return offer.refundable ? 1 : offer.partial_refundable ? 2 : 3;
  };

  export const OPTION_FILTERS_HEADERS = [
    'options',
    'opcje',
    'opzioni',
    'opciók',
    'možnosti',
  ];

  export interface HotelDetailsPersistenceArgs {
    searchId: string;
    hotel?: Search.HotelDetails;
    locationName: string;
    updatedAt: Date;
    searchUrl: string;
  }

  export namespace OffersTrim {
    export const KEY = 'hotel_offers_trimming';

    export function isTrimming(): boolean {
      const stored = localStorage.getItem(KEY);

      try {
        if (stored == null) {
          throw new Error();
        }

        return !!JSON.parse(stored);
      } catch (e) {
        return true;
      }
    }

    export function store(value: boolean) {
      localStorage.setItem(KEY, value ? '1' : '0');
    }
  }
}
